import { useState, useEffect, useRef, useCallback } from 'react';
import { journalAPI } from '../services/api';

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Manages journal entry state with database sync.
 * Auto-saves after 2s of inactivity + on blur.
 * Migrates localStorage entries to the server on first load.
 */
export function useJournal(selectedDate, searchQuery) {
  const [journalEntry, setJournalEntry] = useState('');
  const [savingJournal, setSavingJournal] = useState(false);
  const [journalMatchesSearch, setJournalMatchesSearch] = useState(false);
  const journalRef = useRef(null);
  const hasMigrated = useRef(false);
  const debounceTimer = useRef(null);
  const lastSavedContent = useRef('');

  // One-time migration of localStorage journal entries to the database
  useEffect(() => {
    if (hasMigrated.current) return;
    hasMigrated.current = true;

    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('journal_')) {
        const date = key.replace('journal_', '');
        const content = localStorage.getItem(key);
        if (content) entries.push({ date, content });
      }
    }

    if (entries.length > 0) {
      journalAPI.migrateFromLocal(entries)
        .then(() => {
          entries.forEach(e => localStorage.removeItem(`journal_${e.date}`));
        })
        .catch(() => {});
    }
  }, []);

  // Load journal entry from server when date changes
  useEffect(() => {
    const dateStr = getLocalDateString(selectedDate);
    journalAPI.getEntry(dateStr)
      .then(content => {
        setJournalEntry(content);
        lastSavedContent.current = content;
      })
      .catch(() => {
        const saved = localStorage.getItem(`journal_${dateStr}`) || '';
        setJournalEntry(saved);
        lastSavedContent.current = saved;
      });
  }, [selectedDate]);

  // Core save function
  const saveToServer = useCallback(async (content) => {
    if (content === lastSavedContent.current) return;
    setSavingJournal(true);
    try {
      const dateStr = getLocalDateString(selectedDate);
      await journalAPI.saveEntry(dateStr, content);
      lastSavedContent.current = content;
    } catch (error) {
      console.error('Journal save failed:', error);
      const dateStr = getLocalDateString(selectedDate);
      localStorage.setItem(`journal_${dateStr}`, content);
    } finally {
      setTimeout(() => setSavingJournal(false), 500);
    }
  }, [selectedDate]);

  // Debounced auto-save: triggers 2s after user stops typing
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      saveToServer(journalEntry);
    }, 2000);
    return () => clearTimeout(debounceTimer.current);
  }, [journalEntry, saveToServer]);

  // Save immediately on blur (in case user clicks away before debounce fires)
  const handleSaveJournal = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    saveToServer(journalEntry);
  }, [journalEntry, saveToServer]);

  // Save before navigating away (date change or page close)
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [selectedDate]);

  // Auto-resize textarea
  useEffect(() => {
    if (journalRef.current) {
      journalRef.current.style.height = 'auto';
      journalRef.current.style.height = Math.min(journalRef.current.scrollHeight, 400) + 'px';
    }
  }, [journalEntry]);

  // Check if current journal matches search query
  useEffect(() => {
    if (searchQuery.trim() && journalEntry) {
      setJournalMatchesSearch(journalEntry.toLowerCase().includes(searchQuery.toLowerCase()));
    } else {
      setJournalMatchesSearch(false);
    }
  }, [searchQuery, journalEntry]);

  return {
    journalEntry,
    setJournalEntry,
    savingJournal,
    journalMatchesSearch,
    journalRef,
    handleSaveJournal,
  };
}
